import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import GridOnIcon from "@mui/icons-material/GridOn";
import { getEquipments } from "../api/equipment";
import api from "../api/api";
import { saveAs } from "file-saver";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

function utcToLocalString(str) {
  if (!str || typeof str !== "string") return "-";
  const clean = str.replace("Z", "").replace(".000", "").trim();

  let datePart = "";
  let timePart = "";
  if (clean.includes("T")) {
    [datePart, timePart] = clean.split("T");
  } else if (clean.includes(" ")) {
    [datePart, timePart] = clean.split(" ");
  } else {
    return "-";
  }

  if (!datePart || !timePart) return "-";
  const [y, m, d] = datePart.split("-");
  const [hh = 0, mm = 0, ss = 0] = timePart.split(":");
  if (!y || !m || !d) return "-";

  const dt = new Date(Date.UTC(+y, +m - 1, +d, +hh, +mm, +ss));
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("pt-BR");
}

function ellipsis(text, max = 24) {
  return text && text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

const formatos = [
  { key: "xlsx", label: "Excel (Dados brutos)", icon: <GridOnIcon sx={{ color: "teal" }} /> },
];

const PERFIS = {
  HABITAT: ["CO2", "PM1", "PM2_5", "PM10", "VOC", "NOx", "Temp", "Umid", "pressao"],
  MED: ["CO2", "PM1", "PM2_5", "PM10", "VOC_ppm", "HCHO", "Temp", "Umid", "pressao"],
  PRO: ["PM1", "PM2_5", "PM10", "CO", "H2S", "VOC", "NOx", "Temp", "Umid"],
};

const parametrosBase = [
  { key: "CO2", label: "CO₂" },
  { key: "pressao", label: "Pressão" },
  { key: "Temp", label: "Temperatura" },
  { key: "Umid", label: "Umidade" },
  { key: "PM1", label: "MP1" },
  { key: "PM2_5", label: "MP2.5" },
  { key: "PM10", label: "MP10" },
  { key: "VOC_ppm", label: "VOC (ppm)" },
  { key: "HCHO", label: "Formaldeído" },
  { key: "VOC", label: "VOC (index)" },
  { key: "NOx", label: "NOx" },
  { key: "CO", label: "CO" },
  { key: "H2S", label: "H₂S" },
];

function hasValue(v) {
  return v !== null && v !== undefined && v !== "";
}

function localToUTCString(date) {
  if (!date) return "";
  const pad = (n) => n.toString().padStart(2, "0");
  return (
    date.getUTCFullYear() +
    "-" + pad(date.getUTCMonth() + 1) +
    "-" + pad(date.getUTCDate()) +
    " " + pad(date.getUTCHours()) +
    ":" + pad(date.getUTCMinutes()) +
    ":" + pad(date.getUTCSeconds())
  );
}

function normSensor(raw) {
  const s = (raw || "")
    .toString()
    .trim()
    .replace(/\./g, "_")
    .toUpperCase();

  if (s === "PM2_5") return "PM2_5";
  if (s === "VOC_PPM") return "VOC_ppm";
  if (s === "CO_PPM") return "CO";
  if (s === "H2S_PPM") return "H2S";
  if (s === "PRESSAO" || s === "PRESSÃO") return "pressao";
  if (s === "TEMPERATURA") return "Temp";
  if (s === "TEMP") return "Temp";
  if (s === "UMIDADE") return "Umid";
  if (s === "UMID") return "Umid";
  if (s === "NOX") return "NOx";
  if (s === "HCHO") return "HCHO";
  if (s === "VOC") return "VOC";
  if (s === "CO2") return "CO2";
  if (s === "CO") return "CO";
  if (s === "H2S") return "H2S";
  if (s === "PM1") return "PM1";
  if (s === "PM10") return "PM10";
  return s;
}

function inferProfileFromCaps(caps = []) {
  const s = new Set(caps);
  if (s.has("CO") || s.has("H2S")) return "PRO";
  if (s.has("HCHO") || s.has("VOC_ppm")) return "MED";
  return "HABITAT";
}

function defaultParamForProfile(profile, ativos = []) {
  const preferred =
    profile === "PRO"
      ? ["CO", "H2S", "PM2_5", "Temp"]
      : profile === "MED"
      ? ["CO2", "VOC_ppm", "HCHO", "PM2_5"]
      : ["CO2", "PM2_5", "VOC", "NOx"];

  for (const key of preferred) {
    if (ativos.find((a) => a.key === key)) return key;
  }
  return ativos[0]?.key || "";
}

function buildSensorAliasMap(rows = []) {
  const out = {};

  for (const r of rows) {
    const raw = (r?.sensor || "").toString().trim();
    const canon = normSensor(raw);
    if (!canon || out[canon]) continue;

    out[canon] = raw;
  }

  if (!out.PM2_5) out.PM2_5 = "PM2.5";
  if (!out.CO2) out.CO2 = "CO2";
  if (!out.Temp) out.Temp = "Temp";
  if (!out.Umid) out.Umid = "Umid";
  if (!out.PM1) out.PM1 = "PM1";
  if (!out.PM10) out.PM10 = "PM10";
  if (!out.pressao) out.pressao = "pressao";
  if (!out.NOx) out.NOx = "NOx";
  if (!out.HCHO) out.HCHO = "HCHO";

  // Aliases importantes para o relatório
  if (!out.CO) out.CO = "CO_PPM";
  if (!out.H2S) out.H2S = "H2S_PPM";

  // MED legado: muitos equipamentos salvam VOC ppm como VOC puro.
  if (!out.VOC_ppm) out.VOC_ppm = out.VOC || "VOC";
  if (!out.VOC) out.VOC = "VOC";

  return out;
}

function mapParamToApiKey(uiKey, sensorAliasMap = {}) {
  if (sensorAliasMap[uiKey]) return sensorAliasMap[uiKey];

  if (uiKey === "PM2_5") return "PM2.5";
  if (uiKey === "VOC_ppm") return "VOC";
  if (uiKey === "CO") return "CO_PPM";
  if (uiKey === "H2S") return "H2S_PPM";

  return uiKey;
}

export default function Relatorios() {
  const [equipments, setEquipments] = useState([]);
  const [mac, setMac] = useState("");
  const [param, setParam] = useState("CO2");
  const [dataIni, setDataIni] = useState(() => {
    const now = new Date();
    now.setDate(now.getDate() - 1);
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const [dataFim, setDataFim] = useState(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return now;
  });

  const [formato, setFormato] = useState("xlsx");
  const [relatorios, setRelatorios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [deletandoId, setDeletandoId] = useState(null);
  const [parametrosAtivos, setParametrosAtivos] = useState([]);
  const [profile, setProfile] = useState("HABITAT");
  const [sensorAliasMap, setSensorAliasMap] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    getEquipments().then((list) => {
      setEquipments(list);
      setMac((prev) => prev || (list.length ? list[0].mac : ""));
    });
    fetchRelatorios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRelatorios = async () => {
    try {
      setLoading(true);
      setErro("");
      const res = await api.get("/reports");
      setRelatorios(res.data || []);
    } catch (e) {
      setRelatorios([]);
      setErro("Não foi possível listar relatórios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!mac) return;

      try {
        setErro("");

        let detectedProfile = "HABITAT";
        let caps = [];

        try {
          const capsRes = await api.get("/equipment/capabilities");
          const item = (capsRes.data || []).find((x) => x.mac === mac);
          if (item) {
            caps = (item.capabilities || []).map((c) => normSensor(c));
            detectedProfile = item.profile || inferProfileFromCaps(caps);
          }
        } catch (_) {
          // segue com fallback abaixo
        }

        const dataRes = await api.get("/data");
        const all = dataRes.data?.[mac] || {};
        if (all["PM2.5"] != null && all["PM2_5"] == null) all["PM2_5"] = all["PM2.5"];

        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);

        const hist = await api.get("/historic-data", {
          params: {
            mac,
            start_date: start.toISOString().slice(0, 19).replace("T", " "),
            end_date: end.toISOString().slice(0, 19).replace("T", " "),
          },
        });

        const rows = hist.data || [];
        const presentes = new Set(rows.map((r) => normSensor(r.sensor)));
        const aliasMap = buildSensorAliasMap(rows);
        setSensorAliasMap(aliasMap);

        if (!caps.length) {
          caps = Array.from(presentes);
          if (hasValue(all.CO)) caps.push("CO");
          if (hasValue(all.H2S)) caps.push("H2S");
          if (hasValue(all.HCHO)) caps.push("HCHO");
          if (hasValue(all.VOC_ppm)) caps.push("VOC_ppm");
          if (hasValue(all["PM2_5"])) caps.push("PM2_5");
          detectedProfile = inferProfileFromCaps(caps);
        }

        const permitidos = new Set(PERFIS[detectedProfile] || PERFIS.HABITAT);

        const ativos = parametrosBase.filter((p) => {
          if (!permitidos.has(p.key)) return false;

          if (p.key === "VOC_ppm") {
            return presentes.has("VOC_ppm") || (detectedProfile === "MED" && (presentes.has("VOC") || hasValue(all.VOC)));
          }

          if (p.key === "VOC") {
            if (detectedProfile === "MED") return false;
            return presentes.has("VOC") || hasValue(all.VOC);
          }

          if (p.key === "NOx") {
            return presentes.has("NOx") || hasValue(all.NOx);
          }

          if (p.key === "PM2_5") {
            return presentes.has("PM2_5") || hasValue(all.PM2_5) || hasValue(all["PM2.5"]);
          }

          return presentes.has(normSensor(p.key)) || hasValue(all[p.key]);
        });

        setProfile(detectedProfile);
        setParametrosAtivos(ativos);

        if (!ativos.find((a) => a.key === param)) {
          setParam(defaultParamForProfile(detectedProfile, ativos));
        }
      } catch (e) {
        setProfile("HABITAT");
        setParametrosAtivos(parametrosBase.filter((p) => PERFIS.HABITAT.includes(p.key)));
        setSensorAliasMap({});
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mac]);

  const gerarRelatorio = async () => {
    setErro("");
    setLoading(true);
    try {
      const apiKey = mapParamToApiKey(param, sensorAliasMap);

      await api.post("/generate-report", {
        mac,
        parametro: apiKey,
        dataIni: localToUTCString(dataIni),
        dataFim: localToUTCString(dataFim),
        formato,
      });

      await fetchRelatorios();
    } catch (e) {
      setErro("Erro ao solicitar geração do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const baixarRelatorio = async (rel) => {
    try {
      setLoading(true);
      setErro("");

      const response = await api.get(`/reports/${rel.id}/download`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/octet-stream" });
      saveAs(blob, rel.nome || `relatorio_${rel.id}.xlsx`);
    } catch {
      setErro("Erro ao baixar relatório.");
    } finally {
      setLoading(false);
    }
  };

  const removerRelatorio = async (rel) => {
    if (!window.confirm(`Remover o relatório "${rel.nome}"?`)) return;
    setDeletandoId(rel.id);
    setErro("");
    try {
      await api.delete(`/reports/${rel.id}`);
      await fetchRelatorios();
    } catch {
      setErro("Erro ao remover relatório.");
    } finally {
      setDeletandoId(null);
    }
  };

  const parametroLabelAtual = useMemo(() => {
    const p = parametrosBase.find((x) => x.key === param);
    if (!p) return param;
    return p.label;
  }, [param]);

  const profileLabel = useMemo(() => {
    if (profile === "PRO") return "Perfil detectado: PRO (CO/H₂S)";
    if (profile === "MED") return "Perfil detectado: MED (HCHO/VOC ppm)";
    return "Perfil detectado: Habitat";
  }, [profile]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1250, margin: "0 auto" }}>
      <Typography variant={isMobile ? "h6" : "h4"} gutterBottom>
        Geração e download de relatórios técnicos
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 3 }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
          Gerar Novo Relatório
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 2 },
            alignItems: "center",
            mb: 2,
          }}
        >
          <FormControl sx={{ minWidth: 150, flex: 1, maxWidth: 300 }}>
            <InputLabel>Equipamento</InputLabel>
            <Select
              value={mac}
              label="Equipamento"
              onChange={(e) => setMac(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              {equipments.map((e) => (
                <MenuItem key={e.mac} value={e.mac}>
                  {ellipsis(e.location || e.nomeExibicao || e.mac, 20)} ({e.mac})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140, flex: 1, maxWidth: 220 }}>
            <InputLabel>Parâmetro</InputLabel>
            <Select
              value={param}
              label="Parâmetro"
              onChange={(e) => setParam(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              {parametrosAtivos.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateTimePicker
            label="Data/Hora Inicial"
            value={dataIni}
            onChange={setDataIni}
            sx={{ minWidth: 110, maxWidth: 170 }}
            slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
          />

          <DateTimePicker
            label="Data/Hora Final"
            value={dataFim}
            onChange={setDataFim}
            sx={{ minWidth: 110, maxWidth: 170 }}
            slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
          />

          <FormControl sx={{ minWidth: 120, flex: 1, maxWidth: 200 }}>
            <InputLabel>Formato</InputLabel>
            <Select
              value={formato}
              label="Formato"
              onChange={(e) => setFormato(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              {formatos.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  {f.icon} {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            onClick={gerarRelatorio}
            sx={{ minWidth: 140, height: 46, fontWeight: 600 }}
            disabled={loading || !mac || !param}
            size={isMobile ? "small" : "medium"}
          >
            {loading ? "Gerando..." : "Gerar Relatório"}
          </Button>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          Parâmetro selecionado: <b>{parametroLabelAtual}</b> — Excel: dados brutos para análise personalizada.
        </Typography>

        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={profileLabel}
            color={profile === "PRO" ? "warning" : profile === "MED" ? "success" : "info"}
            variant="outlined"
          />
        </Box>
      </Paper>

      <Typography variant={isMobile ? "subtitle1" : "h6"} mb={2}>
        Relatórios Disponíveis
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Observação:</strong> Os relatórios Excel exibem os horários em <strong>UTC</strong> e em <strong>horário local</strong> (Brasília).
        <br />
        Exemplo: 20h (UTC) &rarr; 17h (Brasília, UTC-3).
      </Alert>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            maxWidth: "100%",
            overflowX: "auto",
            borderRadius: 2,
            boxShadow: 2,
            mb: 5,
          }}
        >
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell>Parâmetro</TableCell>
                <TableCell>Início</TableCell>
                <TableCell>Fim</TableCell>
                <TableCell>Formato</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ação</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {relatorios.map((rel) => (
                <TableRow key={rel.id} hover>
                  <TableCell>
                    <Tooltip title={rel.nome || ""}>
                      <span>{ellipsis(rel.nome || `Relatório ${rel.id}`, 28)}</span>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Tooltip title={rel.location || rel.equipamento || rel.ambiente || rel.mac || ""}>
                      <span>{ellipsis(rel.location || rel.equipamento || rel.ambiente || rel.mac || "-", 18)}</span>
                    </Tooltip>
                  </TableCell>

                  <TableCell>{rel.parametro || "-"}</TableCell>

                  <TableCell>
                    <Tooltip
                      title={
                        (rel.dataIni ? `UTC: ${rel.dataIni}` : "") +
                        (rel.dataIni ? `\nLocal: ${utcToLocalString(rel.dataIni)}` : "")
                      }
                      arrow
                      placement="top"
                    >
                      <span>{rel.dataIni ? utcToLocalString(rel.dataIni) : "-"}</span>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Tooltip
                      title={
                        (rel.dataFim ? `UTC: ${rel.dataFim}` : "") +
                        (rel.dataFim ? `\nLocal: ${utcToLocalString(rel.dataFim)}` : "")
                      }
                      arrow
                      placement="top"
                    >
                      <span>{rel.dataFim ? utcToLocalString(rel.dataFim) : "-"}</span>
                    </Tooltip>
                  </TableCell>

                  <TableCell>{(rel.formato || "").toUpperCase()}</TableCell>

                  <TableCell>
                    <Chip
                      label={rel.status || "-"}
                      color={(rel.status || "").toLowerCase() === "pronto" ? "success" : "warning"}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {(rel.status || "").toLowerCase() === "pronto" && (
                        <Tooltip title="Baixar relatório" arrow>
                          <span>
                            <IconButton
                              color="primary"
                              onClick={() => baixarRelatorio(rel)}
                              disabled={loading}
                              size="small"
                            >
                              <CloudDownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      <Tooltip title="Remover relatório" arrow>
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => (deletandoId ? null : removerRelatorio(rel))}
                            disabled={deletandoId === rel.id}
                            size="small"
                          >
                            {deletandoId === rel.id ? (
                              <CircularProgress size={22} color="error" />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
